// Grupo de estudos (0.71, texto 09 Parte J): o dono compartilha um fichário e os colegas do grupo VEEM NA TELA.
// - Quem criou o grupo a partir de um fichário (⋮ do fichário > Grupo de estudos) publica uma cópia de leitura dele
//   na tabela estudos_grupo (sem cronômetro, sessões, metas nem "revisada", que são pessoais).
// - Imagens e arquivos vão para o balde PRIVADO "estudos-grupo". Só os administradores do grupo leem o balde
//   (regras no servidor, migracao-0.71.sql). Os colegas não conseguem baixar o original nem gerar link:
//   o app pede cada imagem/PDF à função "estudos-ver", que confere se a pessoa está no grupo e se o arquivo
//   faz parte do fichário compartilhado, e devolve o conteúdo só para mostrar na tela (nada é salvo no aparelho).
import { useEffect, useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from './supabase';
import { DEMO } from './env';
import { getDoc } from '../store/store';
import { blocosDaPagina } from '../store/estudos';
import { estaArquivado } from './organizar';
import { lerBase64, temLocal } from './anexos';
import { gruposEmCache, meuId, type MeuGrupo } from './grupos';
import type { Anexo, BlocoPag, Fichario, Materia, Pagina, Quadro } from '../store/types';

const BALDE = 'estudos-grupo';
const LIMITE_ARQ = 25 * 1024 * 1024;

/** Fichário como o colega recebe: só leitura. Anexos chegam só com nome, tipo e o caminho no grupo (gp). */
export type FicharioDoGrupo = { grupo_id: string; fichario: Fichario; dono_nome: string; atualizado_em: string };

// ─── Grupos de Estudos em que participo sem ser administrador ───
export const gruposConvidadoEstudos = (lista: MeuGrupo[] | null | undefined) => (lista ?? []).filter(g => g.aba === 'estudos' && !g.admin);
/** Participa de algum grupo de Estudos (sem administrar)? Decide a tela de convidado (B11 do texto 02). */
export const participaDeEstudos = (lista: MeuGrupo[] | null | undefined) => gruposConvidadoEstudos(lista).length > 0;

// ─── Cópia de leitura (o que o colega recebe) ───
function anexoDeLeitura(a: Anexo | undefined, gp: string | undefined): Anexo {
  // nunca vão: o endereço no aparelho, o link do Drive/OneDrive, o conteúdo embutido nem o que está no cofre
  const out: Anexo = { nome: a?.nome || 'arquivo' };
  if (a?.mime) out.mime = a.mime;
  if (a?.tamanho) out.tamanho = a.tamanho;
  if (gp) out.gp = gp;
  return out;
}
const quadroDeLeitura = (q: Quadro | null | undefined, gpDe: (a: Anexo) => string | undefined): Quadro | undefined =>
  q ? { ...q, imagens: (q.imagens ?? []).map(im => ({ ...im, arq: anexoDeLeitura(im.arq, gpDe(im.arq)) })) } : undefined;

function blocoDeLeitura(b: BlocoPag, gpDe: (a: Anexo) => string | undefined): BlocoPag {
  if (b.tipo === 'imagem' || b.tipo === 'arquivo') return { ...b, arq: anexoDeLeitura(b.arq, gpDe(b.arq)) };
  if (b.tipo === 'desenho') return { ...b, quadro: b.quadro ? quadroDeLeitura(b.quadro, gpDe) ?? null : b.quadro };
  return b;
}
function paginaDeLeitura(p: Pagina, gpDe: (a: Anexo) => string | undefined): Pagina {
  return { id: p.id, titulo: p.titulo, texto: p.texto, revisada: false, updatedAt: p.updatedAt, criadoEm: p.criadoEm,
    blocos: blocosDaPagina(p).map(b => blocoDeLeitura(b, gpDe)), quadro: quadroDeLeitura(p.quadro, gpDe) };
}
/** Só o que está à vista (sem as matérias e páginas arquivadas), sem metas e sem "revisada". */
export function ficharioDeLeitura(f: Fichario, gpDe: (a: Anexo) => string | undefined): Fichario {
  const materias: Materia[] = f.materias.filter(m => !estaArquivado('estudos-materia', m.id)).map(m => ({
    id: m.id, nome: m.nome, icon: m.icon, categoria: m.categoria, metaMin: 0, ordemArq: m.ordemArq,
    paginas: m.paginas.filter(p => !estaArquivado('estudos-pagina', p.id)).map(p => paginaDeLeitura(p, gpDe)),
  }));
  return { id: f.id, nome: f.nome, icon: f.icon, cor: f.cor, materias };
}

/** Todos os anexos do fichário (blocos, quadro e desenhos). */
function anexosDoFichario(f: Fichario): Anexo[] {
  const out: Anexo[] = [];
  const doQuadro = (q?: Quadro | null) => { for (const im of q?.imagens ?? []) out.push(im.arq); };
  for (const m of f.materias) for (const p of m.paginas) {
    doQuadro(p.quadro);
    for (const b of blocosDaPagina(p)) {
      if (b.tipo === 'imagem' || b.tipo === 'arquivo') out.push(b.arq);
      else if (b.tipo === 'desenho') doQuadro(b.quadro);
    }
  }
  return out;
}

// ─── Publicar (aparelho de quem tem o fichário) ───
const chaveAnexo = (a: Anexo) => {
  const s = `${a.nome}|${a.uri ?? ''}|${a.tamanho ?? ''}|${a.dado ? a.dado.length + ':' + a.dado.slice(-120) : ''}`;
  let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
type Estado = { enviados: Record<string, Record<string, string>>; assin: Record<string, string>; ult: Record<string, string> };
let est: Estado = { enviados: {}, assin: {}, ult: {} };
let chaveEst = '';
async function carregarEstado(eu: string) {
  if (chaveEst === `estgrupo:${eu}`) return;
  chaveEst = `estgrupo:${eu}`;
  est = { enviados: {}, assin: {}, ult: {} };
  try { const s = await AsyncStorage.getItem(chaveEst); if (s) est = { ...est, ...JSON.parse(s) }; } catch { /* começa do zero */ }
}
const salvarEstado = () => AsyncStorage.setItem(chaveEst, JSON.stringify(est)).catch(() => {});

export function deB64(b64: string): Uint8Array {
  const tab = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const limpo = b64.replace(/[^A-Za-z0-9+/]/g, '');
  const out = new Uint8Array(Math.floor((limpo.length * 3) / 4));
  let o = 0, buf = 0, bits = 0;
  for (const ch of limpo) { buf = (buf << 6) | tab.indexOf(ch); bits += 6; if (bits >= 8) { bits -= 8; out[o++] = (buf >> bits) & 0xff; } }
  return out.subarray(0, o);
}
const assinatura = (v: unknown) => { const s = JSON.stringify(v); let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return `${s.length}:${h >>> 0}`; };

async function publicarGrupo(g: MeuGrupo, f: Fichario) {
  const enviados = (est.enviados[g.id] ??= {});
  // 1. arquivos ainda não enviados (só os que estão neste aparelho; os do cofre nunca saem)
  for (const a of anexosDoFichario(f)) {
    const k = chaveAnexo(a);
    if (enviados[k] || a.cofre || !temLocal(a)) continue;
    try {
      const bytes = deB64(await lerBase64(a));
      if (bytes.length > LIMITE_ARQ) continue;
      const nome = (a.nome || 'arquivo').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w.\-]+/g, '_').slice(-60);
      const caminho = `${g.id}/${k}-${nome}`;
      const { error } = await supabase!.storage.from(BALDE).upload(caminho, bytes, { contentType: a.mime || 'application/octet-stream', upsert: true });
      if (!error) enviados[k] = caminho;
    } catch { /* tenta de novo no próximo ciclo */ }
  }
  // 2. a cópia de leitura
  const copia = ficharioDeLeitura(f, a => enviados[chaveAnexo(a)]);
  const arquivos = [...new Set(anexosDoFichario(copia).map(a => a.gp).filter((x): x is string => !!x))];
  const sig = assinatura([copia, arquivos]);
  if (est.assin[g.id] === sig) return;
  const { error } = await supabase!.from('estudos_grupo').upsert({ grupo_id: g.id, fichario: copia, arquivos }, { onConflict: 'grupo_id' });
  if (error) return;
  est.assin[g.id] = sig;
  salvarEstado();
  // 3. arquivos que saíram do fichário: apaga do balde
  const usados = new Set(arquivos);
  const sobra = Object.entries(enviados).filter(([, gp]) => !usados.has(gp));
  if (sobra.length) {
    const { error: e2 } = await supabase!.storage.from(BALDE).remove(sobra.map(([, gp]) => gp));
    if (!e2) { for (const [k] of sobra) delete enviados[k]; salvarEstado(); }
  }
}
/** O dono tirou o fichário do grupo (ou apagou o grupo): apaga a cópia e os arquivos. */
async function despublicarGrupo(gid: string) {
  const gps = Object.values(est.enviados[gid] ?? {});
  if (gps.length) await supabase!.storage.from(BALDE).remove(gps);
  await supabase!.from('estudos_grupo').delete().eq('grupo_id', gid);
  delete est.enviados[gid]; delete est.assin[gid];
  salvarEstado();
}

// ─── Receber (colegas) ───
let recebidos: FicharioDoGrupo[] | null = null;
let semServidor = false;   // servidor sem a 0.71 (tabela ainda não criada)
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };

async function receber(meus: MeuGrupo[]) {
  const ids = meus.map(g => g.id);
  if (!ids.length) { if (recebidos?.length !== 0) { recebidos = []; avisar(); } return; }
  const { data, error } = await supabase!.from('estudos_grupo').select('grupo_id, fichario, dono_nome, atualizado_em').in('grupo_id', ids);
  if (error) { if (/estudos_grupo|does not exist|schema cache/i.test(error.message ?? '')) { semServidor = true; recebidos = []; avisar(); } return; }
  semServidor = false;
  const novos = (data ?? []) as FicharioDoGrupo[];
  if (assinatura(novos.map(x => [x.grupo_id, x.atualizado_em])) !== assinatura((recebidos ?? []).map(x => [x.grupo_id, x.atualizado_em]))) { recebidos = novos; avisar(); }
  else if (!recebidos) { recebidos = novos; avisar(); }
}

let rodando = false;
/** Chamado no ciclo dos grupos (lib/compartilhar, a cada 30 s com o app aberto). */
export async function cicloEstudosGrupo() {
  if (DEMO || !supabase || rodando) return;
  const cache = gruposEmCache();
  if (!cache.lista || cache.erro) return;
  rodando = true;
  try {
    const eu = await meuId();
    if (!eu) return;
    await carregarEstado(eu);
    const ficharios = getDoc('estudos')?.ficharios ?? [];
    const meusEst = cache.lista.filter(g => g.aba === 'estudos');
    for (const g of meusEst) {
      if (!g.admin) continue;
      const f = g.ref ? ficharios.find(x => x.id === g.ref) : undefined;
      if (f) { try { await publicarGrupo(g, f); } catch { /* sem internet: tenta depois */ } }
      else if (!g.ref && est.assin[g.id]) { try { await despublicarGrupo(g.id); } catch { /* tenta depois */ } }
    }
    // grupos que deixaram de existir: limpa o que este aparelho tinha enviado
    for (const gid of Object.keys(est.assin)) if (!meusEst.some(g => g.id === gid)) { delete est.assin[gid]; delete est.enviados[gid]; salvarEstado(); }
    await receber(gruposConvidadoEstudos(cache.lista));
  } catch { /* tenta de novo depois */ }
  finally { rodando = false; }
}

/** Fichários compartilhados comigo (null = ainda carregando). Pede de novo ao abrir a tela. */
export function useFicharioDoGrupo() {
  useEffect(() => { cicloEstudosGrupo(); }, []);
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
  return { lista: DEMO || !supabase ? [] : recebidos, semServidor };
}

// ─── Ver na tela (imagens e PDFs do grupo) ───
const vistos = new Map<string, { dado: string; mime: string }>();
let tamVistos = 0;
const pedindo = new Map<string, Promise<{ dado: string; mime: string }>>();
/** Conteúdo para mostrar na tela, pela função "estudos-ver" (confere o grupo no servidor). Fica só na memória. */
export function verDoGrupo(gp: string): Promise<{ dado: string; mime: string }> {
  const ja = vistos.get(gp);
  if (ja) return Promise.resolve(ja);
  const p0 = pedindo.get(gp);
  if (p0) return p0;
  const p = (async () => {
    if (!supabase) throw new Error('sem servidor');
    const { data, error } = await supabase.functions.invoke('estudos-ver', { body: { gp } });
    if (error || !data?.ok || typeof data.b64 !== 'string') throw new Error(String(data?.erro ?? error?.message ?? 'indisponível'));
    const mime = String(data.mime || 'application/octet-stream');
    const out = { dado: `data:${mime};base64,${data.b64}`, mime };
    // até uns 40 MB na memória; passou disso, esquece os mais antigos
    tamVistos += out.dado.length;
    while (tamVistos > 40e6 && vistos.size) { const [k, v] = vistos.entries().next().value as [string, { dado: string }]; vistos.delete(k); tamVistos -= v.dado.length; }
    vistos.set(gp, out);
    return out;
  })().finally(() => pedindo.delete(gp));
  pedindo.set(gp, p);
  return p;
}
/** Imagem do grupo já carregada (data:...), ou undefined enquanto chega (a tela se atualiza sozinha). */
export function imagemDoGrupoJa(gp: string | undefined): string | undefined {
  if (!gp) return undefined;
  const ja = vistos.get(gp);
  if (ja) return ja.dado;
  verDoGrupo(gp).then(avisar, () => {});
  return undefined;
}
/** Re-renderiza quando chega uma imagem do grupo. */
export const useImagensDoGrupo = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
